import { useMemo } from "react";

import { useSelectionStore } from "../../state/useSelectionStore";
import { useLayerStore } from "../../state/useLayerStore";
import { HORIZONS, SCENARIOS, useClimateStore } from "../../state/useClimateStore";
import { useUiStore } from "../../state/useUiStore";
import { useAreaClimate, useAreaFires, usePointClimate } from "../../lib/useClimate";
import {
  FIRE_FIRST_YEAR,
  FIRE_SIGNIFICANT_HA,
  HEAT_LABELS,
  HORIZON_YEARS,
  INDICATORS,
  SCENARIO_LABELS,
  WATER_LEVELS,
  changeFromReference,
  formatHectares,
  heatDrivers,
  type Horizon,
  type IndicatorId,
} from "../../lib/climate";
import {
  COAST_DISTANCE_KM,
  GRID_RESOLUTION_M,
  MIN_ELEVATION_M,
  MODEL,
} from "../../lib/climateDefaults";
import { num } from "../../lib/format";
import { CLIMATE_METRICS, type ClimateMetricId } from "../../map/layers/climate";
import { Figure, SectionTitle, Select } from "../ui/Field";
import { TrajectoryChart, TrajectoryKey } from "./TrajectoryChart";

/** Indicators the pane offers, in the order they matter to someone living here. */
const SHOWN: IndicatorId[] = [
  "tropical_nights",
  "heatwave_days",
  "summer_days",
  "mean_temp",
  "cooling_dd",
  "heating_dd",
  "dry_spell_days",
  "heavy_rain_days",
];

/**
 * What the climate here is projected to become.
 *
 * Two resolutions, kept visibly apart. The projection is an ~8 km model cell —
 * right for a commune, meaningless for a building — and the heat island is a
 * city block. Rendering them at the same visual weight would imply the same
 * precision, so the cell distance is printed next to the projected figures and
 * the map draws the grid as squares rather than smoothing it.
 *
 * Flooding is a hazard, not a climate indicator, and it lives in the Risks pane
 * with the rest of the declaration record. It is cross-linked from here rather
 * than duplicated, so there is one place to keep in step.
 */
export function ClimatePanel({ visible }: { visible: boolean }) {
  const selection = useSelectionStore((s) => s.selection);
  const s = useClimateStore();
  const setTab = useUiStore((t) => t.setTab);

  const area = useMemo(
    () => (selection ? { scope: selection.scope, code: selection.code } : null),
    [selection],
  );
  const point = useMemo(
    () => (selection ? { lon: selection.point.lon, lat: selection.point.lat } : null),
    [selection],
  );
  const view = useMemo(
    () => ({
      indicator: s.metric as IndicatorId,
      scenario: s.scenario,
      horizon: s.horizon as Horizon,
    }),
    [s.metric, s.scenario, s.horizon],
  );

  const { isLoading, isError, data, path } = useAreaClimate(area, view);
  const spot = usePointClimate(point);
  const fires = useAreaFires(area);
  const indicator = INDICATORS[view.indicator];

  const change = data
    ? changeFromReference(data.series, view.indicator, view.scenario, view.horizon)
    : null;

  return (
    <div className="px-4 py-3">
      <MapControls />

      <div className="mt-4 border-t border-line pt-3">
        {!data ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            {isLoading
              ? "Loading…"
              : isError
                ? "No climate cell covers this area."
                : "Click an area on the map."}
          </p>
        ) : (
          <>
            <h3 className="truncate text-[13px] font-semibold">{selection?.name}</h3>
            <p className="text-[10px] text-ink-soft">
              {data.exact
                ? `${(GRID_RESOLUTION_M / 1000).toFixed(0)} km model cell, ${num(
                    data.grid_distance_m ?? 0,
                  )} m away`
                : "averaged across the department's cells"}
              {data.resolved_from === "iris" &&
                " · far coarser than a neighbourhood, so both read the same"}
            </p>

            <div className="mt-3">
              <SectionTitle>{indicator.label}</SectionTitle>
              {change && (
                <div className="grid grid-cols-3 gap-2">
                  <Figure
                    label="1976–2005"
                    value={indicator.format(change.from)}
                    sub={indicator.short}
                  />
                  <Figure
                    label={HORIZON_YEARS[view.horizon].label}
                    value={indicator.format(change.to)}
                    sub={indicator.short}
                    accent
                  />
                  <Figure
                    label="Change"
                    value={`${change.delta >= 0 ? "+" : "−"}${indicator.format(
                      Math.abs(change.delta),
                    )}`}
                    sub={SCENARIO_LABELS[view.scenario] ?? view.scenario}
                  />
                </div>
              )}
              {visible && path.length > 0 && (
                <div className="mt-2">
                  <TrajectoryChart points={path} indicator={indicator} />
                  <TrajectoryKey />
                </div>
              )}
              {indicator.meaning && (
                <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                  {indicator.meaning}
                </p>
              )}
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Heat island at this spot</SectionTitle>
              {spot.isLoading ? (
                <div className="h-16 animate-pulse rounded-lg bg-surface-alt" />
              ) : spot.data?.heat_island ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Figure
                      label="By day"
                      value={HEAT_LABELS[spot.data.heat_island.day_class] ?? "—"}
                      sub="surfaces and shade"
                      accent={spot.data.heat_island.day_class >= 3}
                    />
                    <Figure
                      label="By night"
                      value={HEAT_LABELS[spot.data.heat_island.night_class] ?? "—"}
                      sub="stored heat"
                      accent={spot.data.heat_island.night_class >= 3}
                      hint="Night-time heat is the mass that spent all day storing it, and it is what stops you sleeping."
                    />
                  </div>
                  {heatDrivers(spot.data.heat_island) && (
                    <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                      This block: {heatDrivers(spot.data.heat_island)}.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-[11px] leading-snug text-ink-soft">
                  This block was not assessed — the classification covers built-up
                  ground, and open country has no heat island to speak of.
                </p>
              )}
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Water</SectionTitle>
              <p className="text-[11px] leading-snug">
                {spot.data?.water_restriction?.level ? (
                  <>
                    In force today:{" "}
                    <span className="font-semibold">
                      {WATER_LEVELS[spot.data.water_restriction.level] ??
                        spot.data.water_restriction.level}
                    </span>
                    {spot.data.water_restriction.type && (
                      <span className="text-ink-soft">
                        {" "}
                        on {spot.data.water_restriction.type}
                      </span>
                    )}
                    {spot.data.water_restriction.household_restrictions > 0 && (
                      <span className="text-ink-soft">
                        {" · "}
                        {spot.data.water_restriction.household_restrictions} household
                        uses restricted
                      </span>
                    )}
                  </>
                ) : spot.isLoading ? (
                  <span className="text-ink-soft">Checking…</span>
                ) : (
                  <span className="text-ink-soft">No restriction in force today.</span>
                )}
              </p>
              <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                The history worth reading is the drought declaration record, which
                is on the{" "}
                <button
                  onClick={() => setTab("risks")}
                  className="font-medium text-accent hover:underline"
                >
                  Risks pane
                </button>
                . Longer dry spells above are what turn that into cracked walls.
              </p>
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Fire</SectionTitle>
              {fires.isLoading ? (
                <div className="h-16 animate-pulse rounded-lg bg-surface-alt" />
              ) : fires.data && fires.data.recorded > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <Figure
                      label="Recorded"
                      value={num(fires.data.recorded)}
                      sub={`${fires.data.significant} over ${FIRE_SIGNIFICANT_HA} ha`}
                      hint="Every ignition the national database holds since 2006, down to a square metre."
                    />
                    <Figure
                      label="Burned"
                      value={formatHectares(fires.data.hectares)}
                      sub={`since ${FIRE_FIRST_YEAR}`}
                      hint="Total area, which is the measure that survives changes in reporting effort."
                    />
                    <Figure
                      label="Largest"
                      value={formatHectares(fires.data.largest_ha)}
                      sub={
                        fires.data.last_year ? `last fire ${fires.data.last_year}` : "—"
                      }
                      accent={fires.data.largest_ha >= 5}
                    />
                  </div>
                  {fires.data.worst.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {fires.data.worst.map((f) => (
                        <li
                          key={`${f.year}-${f.started}`}
                          className="flex items-baseline gap-2"
                          title={f.cause ?? undefined}
                        >
                          <span className="w-8 shrink-0 text-[11px] tabular-nums text-ink-soft">
                            {f.year}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[11px] text-ink-soft">
                            {f.started.slice(0, 10)}
                          </span>
                          <span className="shrink-0 text-[11px] font-semibold tabular-nums">
                            {formatHectares(f.ha)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                    Read the area, not the count. The region records one fire in
                    2006 and sixty-seven in 2020 — that is a forest office that
                    started logging every ignition, not a region burning sixty
                    times more often.
                  </p>
                </>
              ) : (
                <p className="text-[11px] leading-snug text-ink-soft">
                  No forest fire recorded here since {FIRE_FIRST_YEAR}. Absence
                  of a record is weaker than it sounds — small fires are logged
                  where someone logs them.
                </p>
              )}
              <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
                Île-de-France is not among the 48 departments where clearing
                around a building is a legal obligation, so there is nothing to
                disclose and no recurring cost. What rises instead is fire
                weather next to a large massif — Fontainebleau, Rambouillet —
                which the dry-spell and summer-day indicators above track.
              </p>
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Sea level and coastal erosion</SectionTitle>
              <p className="text-[11px] leading-snug text-ink-soft">
                Not applicable here. The region is landlocked — roughly{" "}
                {COAST_DISTANCE_KM} km from the nearest coast, and its lowest
                ground sits about {MIN_ELEVATION_M} m above sea level, where the
                Seine leaves for Normandy. Neither shoreline retreat nor marine
                submersion reaches any property in this app, so there is no
                figure to show rather than a zero to misread.
              </p>
            </div>

            <p className="mt-4 border-t border-line pt-2 text-[10px] leading-snug text-ink-soft">
              Scenarios, not forecasts. {MODEL}, on a{" "}
              {(GRID_RESOLUTION_M / 1000).toFixed(0)} km grid — about 190 cells
              cover the whole region, so read these for a city and never for a
              street. Which pathway the world takes is a choice, not a
              prediction, which is why the band is drawn instead of one line.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** What the map is shaded by, and what the figures below are read at. */
function MapControls() {
  const metric = useClimateStore((s) => s.metric);
  const scenario = useClimateStore((s) => s.scenario);
  const horizon = useClimateStore((s) => s.horizon);
  const heatWhen = useClimateStore((s) => s.heatWhen);
  const setMetric = useClimateStore((s) => s.setMetric);
  const setScenario = useClimateStore((s) => s.setScenario);
  const setHorizon = useClimateStore((s) => s.setHorizon);
  const setHeatWhen = useClimateStore((s) => s.setHeatWhen);

  const isVisible = useLayerStore((s) => s.isVisible);
  const setVisible = useLayerStore((s) => s.setVisible);
  const unavailable = useLayerStore((s) => s.unavailable);
  const missing = unavailable.includes("climate_grid");

  return (
    <section>
      <div className="grid grid-cols-2 gap-2">
        <Select
          label="Indicator"
          value={metric}
          options={SHOWN.filter((id) => id in CLIMATE_METRICS).map((id) => ({
            value: id as ClimateMetricId,
            label: INDICATORS[id].label,
          }))}
          onChange={setMetric}
        />
        <Select
          label="Horizon"
          value={horizon}
          options={HORIZONS.map((h) => ({
            value: h,
            label: HORIZON_YEARS[h as Horizon].label,
          }))}
          onChange={setHorizon}
        />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Select
          label="Scenario"
          value={scenario}
          options={SCENARIOS.map((sc) => ({
            value: sc,
            label: `${sc.replace("RCP", "")} — ${SCENARIO_LABELS[sc]}`,
          }))}
          onChange={setScenario}
          hint="Emissions pathways, not likelihoods. None of them is a prediction."
        />
        <Select
          label="Heat island"
          value={heatWhen}
          options={[
            { value: "day" as const, label: "Daytime" },
            { value: "night" as const, label: "Night-time" },
          ]}
          onChange={setHeatWhen}
          hint="A separate, far finer dataset — and not a projection."
        />
      </div>
      {missing ? (
        <p className="mt-1.5 text-[10px] text-ink-soft">
          Climate tiles not built — run <code>make climate</code>.
        </p>
      ) : (
        !isVisible("climate_grid") && (
          <button
            onClick={() => setVisible("climate_grid", true)}
            className="mt-1.5 text-[10px] font-medium text-accent hover:underline"
          >
            Show it on the map
          </button>
        )
      )}
    </section>
  );
}
